import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { APIProvider } from "@vis.gl/react-google-maps";
import { brandQueryOptions } from "@/api/vendure/merchant/brand.query.options";
import { VenueLocator } from "@/components/brand/VenueLocator";
import { CompactLoader } from "@/components/ui/loader";
import { useGeolocation } from "@/hooks/useGeolocation";
import { getSiteOrigin } from "@/lib/origin";
import {
  absoluteUrl,
  buildBrandJsonLd,
  serializeJsonLd,
  truncateDescription,
} from "@/lib/seo";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;

export const Route = createFileRoute("/brand/$slug")({
  ssr: "data-only",
  loader: async ({ params: { slug }, context: { queryClient } }) => {
    const brand = await queryClient
      .ensureQueryData(brandQueryOptions(slug))
      .catch(() => {
        throw notFound();
      });
    if (!brand || !brand.venues || brand.venues.length === 0) throw notFound();
    return { brand, origin: getSiteOrigin() };
  },
  head: ({ loaderData, match }) => {
    if (!loaderData) return {};
    const { brand, origin } = loaderData;
    const url = absoluteUrl(origin, `/brand/${match.params.slug}`);
    const title = `${brand.name} — Locations & Online Ordering`;
    const description = truncateDescription(
      `Find a ${brand.name} venue near you and order online. ${brand.venues.length} locations available.`,
    );
    const image = absoluteUrl(
      origin,
      brand.venues[0]?.image_url ?? "/logo.png",
    );
    return {
      links: [
        { rel: "canonical", href: url },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      ],
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: brand.name },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: serializeJsonLd(buildBrandJsonLd({ brand, origin, url })),
        },
      ],
    };
  },
  pendingComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <CompactLoader />
    </div>
  ),
  component: Sentry.withProfiler(BrandPage, { name: "BrandPage" }),
});

function BrandPage() {
  const { slug } = Route.useParams();
  const { brand: initialBrand } = Route.useLoaderData();
  const { coords } = useGeolocation();

  const { data } = useQuery(brandQueryOptions(slug, coords));
  const brand = data ?? initialBrand;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <VenueLocator brand={brand} userCoords={coords} />
    </APIProvider>
  );
}
